import { AnyAction } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import {
  AdressvorschlagFailedPayload,
  AdressvorschlagPayload,
  CALLER_ID,
  CheckoutProdukt,
  GlobalActions,
  PhCheckoutProduktPayload,
  ProduktDeleted,
} from '@ps20-frontend/shop-integration-redux';
import { Adressvorschlag, App, Immobilie } from '@ps20-frontend/frontend-models';
import { updateObjectInArray } from '@ps20-frontend/reducers';
import { CheckoutActions } from '../checkout.actions';
import { VermieteteWohnungChanged } from '../action-types/vermieteteWohnungChanged';
import { ImmobilieUebernommen } from '../action-types/immobilieUebernommen';
import { IAppState } from '../localStore';

export interface VermieteteWohnung {
  plz: string;
  ort: string;
  strasse: string;
  hausnummer: string;
  wohnungsnummer: string;
  selectedKdvImmobilienId: string | null;
  adressvorschlag: Adressvorschlag;
}

export function initVermieteteWohnung(): VermieteteWohnung {
  return {
    plz: '',
    hausnummer: '',
    ort: '',
    strasse: '',
    wohnungsnummer: '',
    selectedKdvImmobilienId: null,
    adressvorschlag: null,
  };
}

export const vermieteteWohnungenReducer = function (
  sectionState: Array<VermieteteWohnung> = null,
  action: AnyAction,
  state: IAppState,
): Array<VermieteteWohnung> {
  switch (action.type) {
    case CheckoutActions.VERMIETETE_WOHNUNG_CHANGED: {
      const newState = cloneDeep(sectionState);
      const actionPayload: VermieteteWohnungChanged = action.payload;
      newState[actionPayload.index] = actionPayload.wohnung;
      return newState;
    }
    case GlobalActions.PRODUKT_ACTION: {
      const produkt: CheckoutProdukt = action.payload;

      if (produkt.appId === App.PH) {
        return updateVermieteteWohnungen(produkt, sectionState);
      }
      return sectionState;
    }
    case CheckoutActions.IMMOBILIE_UEBERNOMMEN: {
      const newState: Array<VermieteteWohnung> = cloneDeep(sectionState);
      const actionPayload: ImmobilieUebernommen = action.payload;
      updateVermieteteWohnungWithImmobilie(state, actionPayload, newState);

      return newState;
    }
    case GlobalActions.PRODUKT_DELETED: {
      const payload: ProduktDeleted = action.payload;
      if (payload.angebotsIds.some(angebotsId => angebotsId.appId === App.PH)) {
        return [];
      }
      return sectionState;
    }

    case GlobalActions.ADDRESSVORSCHLAG_SUCCESSFUL: {
      const payload: AdressvorschlagPayload = action.payload;
      if (payload.callerId !== CALLER_ID.VERMIETETE_WOHNUNG) {
        return sectionState;
      }

      const vermieteteWohnung: Partial<VermieteteWohnung> = {
        adressvorschlag: {
          lastError: null,
          orte: payload.orte ? payload.orte : [],
          strassen: payload.strassen ? payload.strassen : [],
        },
      };

      if (payload.orte?.length === 1) {
        vermieteteWohnung.ort = payload.orte[0];
      }

      return updateObjectInArray(sectionState, payload.callerData.index, vermieteteWohnung);
    }
    case GlobalActions.ADDRESSVORSCHLAG_FAILED: {
      const payload: AdressvorschlagFailedPayload = action.payload;
      if (payload.callerId !== CALLER_ID.VERMIETETE_WOHNUNG) {
        return sectionState;
      }
      const vermieteteWohnung: Partial<VermieteteWohnung> = {
        adressvorschlag: { lastError: payload.reason, orte: [], strassen: [] },
      };

      return updateObjectInArray(sectionState, payload.callerData.index, vermieteteWohnung);
    }
    default:
      return sectionState;
  }
};

function updateVermieteteWohnungen(
  produkt: CheckoutProdukt,
  sectionState: Array<VermieteteWohnung>,
): Array<VermieteteWohnung> {
  const phProduktPayload = produkt as PhCheckoutProduktPayload;
  let newState = cloneDeep(sectionState);
  const newAnzahl = phProduktPayload.anzahlVersicherterWohnungen;
  const currentAnzahl = newState == null ? 0 : newState.length;
  if (currentAnzahl < newAnzahl) {
    addNewInitialWohnungen(newAnzahl - currentAnzahl, newState);
  } else if (currentAnzahl > newAnzahl) {
    newState = newState.slice(0, newAnzahl);
  }

  return newState;
}

function addNewInitialWohnungen(anzahl: number, currentWohnungen: Array<VermieteteWohnung>): void {
  for (let index = 0; index < anzahl; index++) {
    currentWohnungen.push(initVermieteteWohnung());
  }
}

function updateVermieteteWohnungWithImmobilie(
  state: IAppState,
  actionPayload: ImmobilieUebernommen,
  newState: Array<VermieteteWohnung>,
): void {
  const selectedImmobilie: Immobilie = state.immobilien.find(
    immobilie => immobilie.id === actionPayload.immoId,
  );
  const immobilieToUpdate = newState[actionPayload.index];

  immobilieToUpdate.strasse = selectedImmobilie.risikoort.strasse;
  immobilieToUpdate.hausnummer = selectedImmobilie.risikoort.hausnummer;
  immobilieToUpdate.plz = selectedImmobilie.risikoort.plz;
  immobilieToUpdate.ort = selectedImmobilie.risikoort.ort;
  immobilieToUpdate.wohnungsnummer = selectedImmobilie.wohnungsnr;
  immobilieToUpdate.strasse = selectedImmobilie.risikoort.strasse;
  immobilieToUpdate.selectedKdvImmobilienId = actionPayload.immoId;
}
